import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { type ImportableNote, ImportStatus, LoadingStatus } from "./types";
import { SectionSelectionState } from "./types";
import type { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import type { FileDropItemWithPath } from "@/modules/file/dropItems";
import { pluralize } from "@/modules/pluralize";
import {
  markImportSessionInProgress,
  clearImportSessionInProgress,
} from "@/modules/cache/import-session";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { objectModule } from "@/modules/object";
import { SortByKind } from "@/modules/lenses/types";
import uFuzzy from "@leeoniya/ufuzzy";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import type { ImportedAppleNote } from "@/modules/electron/types";
import { ImportFileUploaderKind } from "@/store/upload-progress/AppStoreUploadProgressStore";

/**
 * Sort options supported for imported notes
 */
export type ImportedNoteSortBy =
  | SortByKind.LastModified
  | SortByKind.LastCreated
  | SortByKind.Alphabetical;

/**
 * Represents a folder with its associated collection ID for import
 */
export interface ImportableFolder {
  name: string;
  collectionId: Uuid;
}

/**
 * Represents a badge to display for a note (folder or tag)
 */
export interface NoteCollectionBadge {
  type: "folder" | "tag";
  name: string;
  collectionId?: Uuid;
}

/**
 * Represents a folder grouping for display in the import UI.
 * Groups by the last (deepest) folder in the path.
 */
export interface FolderGroup {
  /** The section name (last folder in path, or empty for unfiled) */
  section: string;
  /** Collection IDs for all folders in the path */
  collectionIds: Uuid[];
  notes: ImportableNote[];
}

/**
 * Store for managing one-click import of notes from external sources (e.g., Apple Notes)
 */
export class AppStoreOneClickImportStore extends AppSubStore {
  /**
   * Current status of the import process
   */
  importStatus: ImportStatus = ImportStatus.Idle;

  /**
   * Status of loading notes from Apple Notes
   */
  loadingStatus: LoadingStatus = LoadingStatus.Done;

  /**
   * Error message to display in the modal footer
   */
  errorMessage: string | null = null;

  /**
   * List of notes available for import
   */
  notes: ImportableNote[] = [];

  /**
   * Cached array of note titles for fuzzy search (updated when notes are added)
   */
  private noteTitles: string[] = [];

  /**
   * Whether to use folders as collections when importing
   */
  useFoldersAsCollections = true;

  /**
   * Whether to use tags as collections when importing
   */
  useTagsAsCollections = true;

  /**
   * Selected folder names to convert to collections (INCLUDE list)
   * Empty array means no folders selected, even if useFoldersAsCollections is true
   */
  selectedFolderNames: string[] = [];

  /**
   * Selected tag names to convert to collections (INCLUDE list)
   * Empty array means no tags selected, even if useTagsAsCollections is true
   */
  selectedTagNames: string[] = [];

  /**
   * Whether the import modal is open
   */
  isModalOpen = false;

  /**
   * Whether to reopen the wizard after import modal closes (for onboarding flow)
   */
  shouldReopenWizardAfterImport = false;

  /**
   * The step to show when reopening the wizard (for onboarding flow)
   */
  wizardStepToReopenOn: string | null = null;

  /**
   * The step to fall back to if import fails (for onboarding flow)
   */
  wizardStepToFallbackTo: string | null = null;

  /**
   * Filter query for searching notes by title
   */
  filterQuery = "";

  /**
   * Search query for filtering tags in the dropdown
   */
  tagSearchQuery = "";

  /**
   * Search query for filtering folders in the dropdown
   */
  folderSearchQuery = "";

  /**
   * Sort option for notes in the import list
   */
  sortOption: ImportedNoteSortBy = SortByKind.LastModified;

  /**
   * Map of folder names to their generated collection IDs
   */
  private folderToCollectionId: Map<string, Uuid> = new Map();

  /**
   * Map of tag names to their generated collection IDs
   */
  private tagToCollectionId: Map<string, Uuid> = new Map();

  /**
   * Fuzzy search instance for filtering notes
   */
  private fuzzy: uFuzzy;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.fuzzy = new uFuzzy();

    makeObservable<
      AppStoreOneClickImportStore,
      | "noteTitles"
      | "folderToCollectionId"
      | "tagToCollectionId"
      | "fuzzy"
      | "getOrCreateCollectionId"
      | "getOrCreateTagCollectionId"
      | "getSectionForNote"
      | "createFileDropItemFromNote"
      | "normalizeFolderNameForHashtag"
      | "normalizeTagNameForHashtag"
      | "sanitizeFolderNameForPath"
      | "sanitizeFilename"
    >(this, {
      importStatus: observable,
      loadingStatus: observable,
      errorMessage: observable,
      notes: observable,
      noteTitles: observable,
      useFoldersAsCollections: observable,
      useTagsAsCollections: observable,
      selectedFolderNames: observable,
      selectedTagNames: observable,
      isModalOpen: observable,
      shouldReopenWizardAfterImport: observable,
      wizardStepToReopenOn: observable,
      wizardStepToFallbackTo: observable,
      filterQuery: observable,
      tagSearchQuery: observable,
      folderSearchQuery: observable,
      sortOption: observable,
      folderToCollectionId: observable,
      tagToCollectionId: observable,
      fuzzy: false,
      getOrCreateCollectionId: false,
      getOrCreateTagCollectionId: false,
      getCollectionIdForFolder: false,
      getCollectionIdForTag: false,
      getNotesCountForFolder: false,
      getNotesCountForTag: false,
      getBadgesForNote: false,
      getSectionForNote: false,
      createFileDropItemFromNote: false,
      normalizeFolderNameForHashtag: false,
      normalizeTagNameForHashtag: false,
      sanitizeFolderNameForPath: false,
      sanitizeFilename: false,
      openModal: action,
      closeModal: action,
      clearWizardReopenFlags: action,
      resetImportStatus: action,
      startImport: action,
      loadNotes: action,
      retryLoadNotes: action,
      setLoadingStatus: action,
      setErrorMessage: action,
      clearErrorMessage: action,
      isLoading: computed,
      hasLoadingError: computed,
      setFilterQuery: action,
      setTagSearchQuery: action,
      setFolderSearchQuery: action,
      setSortOption: action,
      toggleUseFoldersAsCollections: action,
      toggleUseTagsAsCollections: action,
      setSelectedFolderNames: action,
      setSelectedTagNames: action,
      toggleNoteSelection: action,
      selectAll: action,
      unselectAll: action,
      toggleSectionSelection: action,
      selectSection: action,
      unselectSection: action,
      addNote: action,
      addNotes: action,
      clear: action,
      importSelectedNotes: action,
      selectedNotes: computed,
      selectedNotesCount: computed,
      totalNotesCount: computed,
      selectableNotesCount: computed,
      sortedNotes: computed,
      filteredNotes: computed,
      folders: computed,
      tags: computed,
      filteredFolders: computed,
      filteredTags: computed,
      importableFolders: computed,
      importableTags: computed,
      notesGroupedByFolder: computed,
      getSectionSelectionState: false,
      setStatus: action,
    });
  }

  /**
   * Open the import modal and initialize selections
   */
  openModal = () => {
    this.isModalOpen = true;
  };

  /**
   * Close the import modal
   */
  closeModal = ({ status }: { status: ImportStatus }) => {
    this.isModalOpen = false;
    this.filterQuery = "";
    this.sortOption = SortByKind.LastModified;
    this.setStatus(status);
  };

  /**
   * Clear the wizard reopen flags (called by parent after reopening wizard)
   */
  clearWizardReopenFlags = () => {
    this.shouldReopenWizardAfterImport = false;
    this.wizardStepToReopenOn = null;
    this.wizardStepToFallbackTo = null;
  };

  /**
   * Reset the import status to idle
   */
  resetImportStatus = () => {
    this.importStatus = ImportStatus.Idle;
  };

  setStatus(status: ImportStatus) {
    this.importStatus = status;
  }

  /**
   * Set the loading status for notes
   */
  setLoadingStatus = (status: LoadingStatus) => {
    this.loadingStatus = status;
  };

  /**
   * Set the error message to display in the modal
   */
  setErrorMessage = (message: string) => {
    this.errorMessage = message;
  };

  /**
   * Clear the error message
   */
  clearErrorMessage = () => {
    this.errorMessage = null;
  };

  /**
   * Whether notes are currently being loaded
   */
  get isLoading(): boolean {
    return this.loadingStatus === LoadingStatus.Loading;
  }

  /**
   * Whether there was an error loading notes
   */
  get hasLoadingError(): boolean {
    return this.loadingStatus === LoadingStatus.Error;
  }

  /**
   * Start the one-click import process for Apple Notes.
   * Opens the modal immediately and starts loading notes in the background.
   * @returns Promise that resolves to true if import started successfully, false otherwise
   */
  startImport = async (): Promise<boolean> => {
    // Open modal immediately with loading state
    this.setStatus(ImportStatus.InProgress);
    this.setLoadingStatus(LoadingStatus.Loading);
    this.openModal();

    // Load notes in the background
    return this.loadNotes();
  };

  /**
   * Load notes from Apple Notes via Electron IPC.
   * This is called internally by startImport and retryLoadNotes.
   * @returns Promise that resolves to true if notes loaded successfully, false otherwise
   */
  loadNotes = async (): Promise<boolean> => {
    try {
      this.setLoadingStatus(LoadingStatus.Loading);

      const result = await electronModule.importAppleNotes();

      if (!result.success) {
        this.setLoadingStatus(LoadingStatus.Error);
        this.setErrorMessage("Failed to load notes from Apple Notes. Please try again.");
        this.setStatus(ImportStatus.Failed);

        return false;
      }

      logger.info({
        message: "[AppStoreOneClickImportStore] Apple Notes imported",
        info: { notesPerTag: result.notesPerTag },
      });

      // Convert ImportedAppleNote[] to ImportableNote[]
      const importableNotes = result.notes.map((note: ImportedAppleNote) => {
        // Convert folderPath (e.g., "Work/Mem/Auto Organize") to folders array
        const folders = note.folderPath
          ? note.folderPath.split("/").filter(Boolean)
          : note.folder
            ? [note.folder]
            : [];

        return {
          title: note.title,
          markdown: note.content,
          folders,
          isSelected: true,
          isLocked: false,
          isPinned: note.isPinned ?? false,
          createdAt: note.createdAt,
          updatedAt: note.modifiedAt,
          tags: note.tags ?? [],
          subtitle: note.content.split("\n")[0]?.substring(0, 100) || undefined,
        };
      });

      // Clear existing notes and add imported ones
      this.clear();
      this.addNotes(importableNotes);

      if (this.selectedFolderNames.length === 0) {
        this.selectedFolderNames = this.folders;
      }

      if (this.selectedTagNames.length === 0) {
        this.selectedTagNames = this.tags;
      }

      // Set to what user has in Apple Notes at the time of import
      this.setSortOption(SortByKind.LastModified);
      this.setLoadingStatus(LoadingStatus.Done);

      return true;
    } catch (error) {
      this.setLoadingStatus(LoadingStatus.Error);
      this.setErrorMessage("Failed to load notes from Apple Notes. Please try again.");
      this.setStatus(ImportStatus.Failed);

      logger.error({
        message: "[AppStoreOneClickImportStore] Failed to import Apple Notes",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });

      return false;
    }
  };

  /**
   * Retry loading notes after a failure
   */
  retryLoadNotes = async (): Promise<boolean> => {
    return this.loadNotes();
  };

  /**
   * Set the filter query for searching notes by title
   */
  setFilterQuery = (query: string) => {
    this.filterQuery = query;
  };

  /**
   * Set the search query for filtering tags
   */
  setTagSearchQuery = (query: string) => {
    this.tagSearchQuery = query;
  };

  /**
   * Set the search query for filtering folders
   */
  setFolderSearchQuery = (query: string) => {
    this.folderSearchQuery = query;
  };

  /**
   * Set the sort option for notes
   */
  setSortOption = (option: ImportedNoteSortBy) => {
    this.sortOption = option;
  };

  /**
   * Toggle whether to use folders as collections
   */
  toggleUseFoldersAsCollections = () => {
    this.useFoldersAsCollections = !this.useFoldersAsCollections;
  };

  /**
   * Toggle whether to use tags as collections
   */
  toggleUseTagsAsCollections = () => {
    this.useTagsAsCollections = !this.useTagsAsCollections;
  };

  /**
   * Set selected folder names for conversion to collections
   */
  setSelectedFolderNames = (folderNames: string[]) => {
    this.selectedFolderNames = folderNames;
  };

  /**
   * Set selected tag names for conversion to collections
   */
  setSelectedTagNames = (tagNames: string[]) => {
    this.selectedTagNames = tagNames;
  };

  /**
   * Toggle selection of a note
   */
  toggleNoteSelection = (noteId: string) => {
    runInAction(() => {
      const note = this.notes.find(n => n.id === noteId);
      if (note && !note.isLocked) {
        note.isSelected = !note.isSelected;
      }
    });
  };

  /**
   * Select all selectable (non-locked) notes
   */
  selectAll = () => {
    runInAction(() => {
      this.notes.forEach(note => {
        if (!note.isLocked) {
          note.isSelected = true;
        }
      });
    });
  };

  /**
   * Unselect all notes
   */
  unselectAll = () => {
    runInAction(() => {
      this.notes.forEach(note => {
        note.isSelected = false;
      });
    });
  };

  /**
   * Toggle selection for all selectable notes in a section.
   * If any notes are unselected, selects all. Otherwise, deselects all.
   */
  toggleSectionSelection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    const selectableNotes = group.notes.filter(note => !note.isLocked);
    const allSelected = selectableNotes.every(note => note.isSelected);

    runInAction(() => {
      selectableNotes.forEach(note => {
        note.isSelected = !allSelected;
      });
    });
  };

  /**
   * Select all selectable notes in a section
   */
  selectSection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    runInAction(() => {
      group.notes.forEach(note => {
        if (!note.isLocked) {
          note.isSelected = true;
        }
      });
    });
  };

  /**
   * Unselect all notes in a section
   */
  unselectSection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    runInAction(() => {
      group.notes.forEach(note => {
        note.isSelected = false;
      });
    });
  };

  /**
   * Get the selection state for a section
   */
  getSectionSelectionState = (section: string): SectionSelectionState => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return SectionSelectionState.None;

    const selectableNotes = group.notes.filter(note => !note.isLocked);
    if (selectableNotes.length === 0) return SectionSelectionState.None;

    const selectedCount = selectableNotes.filter(note => note.isSelected).length;

    if (selectedCount === 0) return SectionSelectionState.None;
    if (selectedCount === selectableNotes.length) return SectionSelectionState.All;
    return SectionSelectionState.Indeterminate;
  };

  /**
   * Get or create a collection ID for a folder name
   */
  private getOrCreateCollectionId = (folderName: string): Uuid => {
    if (!this.folderToCollectionId.has(folderName)) {
      this.folderToCollectionId.set(folderName, uuidModule.generate());
    }
    return this.folderToCollectionId.get(folderName)!;
  };

  /**
   * Get or create a collection ID for a tag name
   */
  private getOrCreateTagCollectionId = (tagName: string): Uuid => {
    if (!this.tagToCollectionId.has(tagName)) {
      this.tagToCollectionId.set(tagName, uuidModule.generate());
    }
    return this.tagToCollectionId.get(tagName)!;
  };

  /**
   * Get the collection ID for a folder name (returns undefined if folder doesn't exist)
   */
  getCollectionIdForFolder = (folderName: string): Uuid | undefined => {
    return this.folderToCollectionId.get(folderName);
  };

  /**
   * Get the collection ID for a tag name (returns undefined if tag doesn't exist)
   */
  getCollectionIdForTag = (tagName: string): Uuid | undefined => {
    return this.tagToCollectionId.get(tagName);
  };

  /**
   * Get the count of notes that have a specific folder
   */
  getNotesCountForFolder = (folderName: string): number => {
    return this.notes.filter(note => note.folders?.includes(folderName)).length;
  };

  /**
   * Get the count of notes that have a specific tag
   */
  getNotesCountForTag = (tagName: string): number => {
    return this.notes.filter(note => note.tags?.includes(tagName)).length;
  };

  /**
   * Get the collection badges that should be displayed for a note.
   * Combines folder and tag badges, filters by enabled toggles and selections,
   * and deduplicates by name (case-insensitive).
   */
  getBadgesForNote = (note: ImportableNote): NoteCollectionBadge[] => {
    const badges: NoteCollectionBadge[] = [];

    // Add folder badges if enabled and in selected list
    if (this.useFoldersAsCollections && note.folders) {
      note.folders.forEach(folder => {
        if (this.selectedFolderNames.includes(folder)) {
          badges.push({
            type: "folder",
            name: folder,
            collectionId: this.getCollectionIdForFolder(folder),
          });
        }
      });
    }

    // Add tag badges if enabled and in selected list
    if (this.useTagsAsCollections && note.tags) {
      note.tags.forEach(tag => {
        if (this.selectedTagNames.includes(tag)) {
          badges.push({
            type: "tag",
            name: tag,
            collectionId: this.getCollectionIdForTag(tag),
          });
        }
      });
    }

    // Deduplicate by name (case-insensitive)
    const seenNames = new Set<string>();
    const uniqueBadges = badges.filter(badge => {
      const lowerName = badge.name.toLowerCase();
      if (seenNames.has(lowerName)) {
        return false;
      }
      seenNames.add(lowerName);
      return true;
    });

    return uniqueBadges;
  };

  /**
   * Get the section name for a note (last folder in path)
   */
  private getSectionForNote = (note: ImportableNote): string => {
    if (!note.folders || note.folders.length === 0) {
      return "";
    }
    return note.folders[note.folders.length - 1];
  };

  /**
   * Sanitize a filename by removing invalid characters
   */
  private sanitizeFilename = (title: string): string => {
    return (
      title
        .replace(/[<>:"/\\|?*]/g, "-")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200) || "Untitled"
    );
  };

  /**
   * Convert an ImportableNote to a FileDropItemWithPath for the import system
   */
  private createFileDropItemFromNote = (note: ImportableNote): FileDropItemWithPath => {
    // Build folder path with sanitized folder names (spaces -> underscores for file paths)
    // ["My Folder", "Work"] => "/My_Folder/Work"
    const sanitizedFolderPath = note.folders?.length
      ? `/${note.folders.map(folder => this.sanitizeFolderNameForPath(folder)).join("/")}`
      : "";
    const filename = `${this.sanitizeFilename(note.title)}.md`;
    const fullPath = `${sanitizedFolderPath}/${filename}`;

    const content = note.markdown;

    // Create File blob with preserved timestamp
    const lastModified = note.updatedAt ? new Date(note.updatedAt).getTime() : Date.now();

    return {
      kind: "file" as const,
      type: "text/markdown",
      name: filename,
      fullPath,
      getFile: async () =>
        new File([content], filename, {
          type: "text/markdown",
          lastModified,
        }),
      getText: async () => content,
    };
  };

  /**
   * Normalize folder name for use in hashtags.
   * Backend regex requires `#[a-zA-Z0-9_-]+`, so we replace spaces with underscores.
   * The collection title will preserve underscores (matching the folder name sanitization).
   */
  private normalizeFolderNameForHashtag = (folderName: string): string | undefined => {
    const trimmed = folderName.trim();
    if (trimmed.length === 0) return undefined;

    // Replace whitespace with underscores, strip unsupported chars to match backend regex.
    const normalized = trimmed.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
    if (normalized.length === 0) return undefined;

    return normalized;
  };

  /**
   * Sanitize folder name for use in file paths (replaces spaces with underscores).
   * This matches the sanitization used in hashtags so collection names stay consistent.
   */
  private sanitizeFolderNameForPath = (folderName: string): string => {
    return folderName.replace(/\s+/g, "_").replace(/[<>:"/\\|?*]/g, "-");
  };

  /**
   * Import selected notes using the session-based import system
   */
  importSelectedNotes = async (): Promise<void> => {
    const notes = this.selectedNotes;
    if (notes.length === 0) return;

    const notesCount = notes.length;

    // Convert ImportableNote[] to FileDropItemWithPath[]
    const fileItems = notes.map(note => this.createFileDropItemFromNote(note));

    // Create session
    const sessionId = await this.store.sessions.createSession({
      title: `Bring in ${notesCount} ${pluralize(notesCount, "note", "notes")} from Apple Notes`,
      status: "PROCESSING",
      kind: "IMPORT",
    });

    await markImportSessionInProgress(sessionId);

    // Create batch with config
    const batchId = await this.store.uploadedFileBatches.createUploadedFileBatch({
      sessionId,
      config: {
        use_filenames_as_titles: true,
        use_folders_as_collections: this.useFoldersAsCollections,
        use_tags_as_collections: this.useTagsAsCollections,
        selected_folder_names: this.useFoldersAsCollections ? [...this.selectedFolderNames] : [],
        selected_tag_names: this.useTagsAsCollections ? [...this.selectedTagNames] : [],
      },
    });

    this.store.sessionEvents.createMessageSentSessionEvent({
      session_id: sessionId,
      message_value: {
        kind: "REASONING",
        content: `Uploading ${notesCount} ${pluralize(notesCount, "note", "notes")}`,
      },
    });

    let uploadedFileIds: string[] = [];
    try {
      uploadedFileIds = await this.store.uploadProgress.uploadFileItems(fileItems, sessionId, {
        uploader: ImportFileUploaderKind.StorageApi,
      });
    } catch (e) {
      await this.store.sessions.updateSessionOnNewUserMessage(sessionId, { status: "ERROR" });
      await clearImportSessionInProgress(sessionId);

      this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: { kind: "REASONING", content: "Failed to upload notes" },
      });

      trackEvent(TrackedEvent.ImportFilesBatchFailed, {
        num_files: notesCount,
        session_id: sessionId,
        context: "oneClickImport",
        error: objectModule.safeErrorAsJson(e as Error),
      });

      throw e;
    } finally {
      await clearImportSessionInProgress(sessionId);
    }

    const uploadedFileResults = this.store.uploadProgress.getFileResults(uploadedFileIds);
    await this.store.uploadedFiles.createUploadedFiles({ batchId, uploadedFileResults });
    await this.store.uploadedFileBatches.addUploadsToBatch(batchId, uploadedFileIds);
    await this.store.uploadedFileBatches.startProcessingBatch(batchId, sessionId);

    trackEvent(TrackedEvent.ImportFilesBatchStarted, {
      num_files: uploadedFileResults.length,
      batch_id: batchId,
      session_id: sessionId,
    });
  };

  /**
   * Add a note to the import list
   */
  addNote = (note: Omit<ImportableNote, "id"> & { id?: string }) => {
    runInAction(() => {
      const newNote: ImportableNote = {
        id: note.id || uuidModule.generate(),
        title: note.title,
        markdown: note.markdown,
        folders: note.folders,
        isSelected: note.isSelected,
        isLocked: note.isLocked,
        lockedReason: note.lockedReason,
        isPinned: note.isPinned ?? false,
        tags: note.tags ?? [],
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
        subtitle: note.subtitle,
      };
      this.notes.push(newNote);
      this.noteTitles.push(newNote.title);

      // Generate collection IDs for all folders in the path
      if (note.folders && note.folders.length > 0) {
        note.folders.forEach(folder => {
          this.getOrCreateCollectionId(folder);
        });
      }

      // Generate collection IDs for all tags
      if (note.tags && note.tags.length > 0) {
        note.tags.forEach(tag => {
          this.getOrCreateTagCollectionId(tag);
        });
      }
    });
  };

  /**
   * Add multiple notes to the import list
   */
  addNotes = (notes: Array<Omit<ImportableNote, "id"> & { id?: string }>) => {
    notes.forEach(note => this.addNote(note));
  };

  /**
   * Clear all notes and reset state
   */
  clear = () => {
    runInAction(() => {
      this.notes = [];
      this.noteTitles = [];
      this.folderToCollectionId.clear();
      this.tagToCollectionId.clear();
      this.useFoldersAsCollections = true;
      this.useTagsAsCollections = true;
      this.selectedFolderNames = [];
      this.selectedTagNames = [];
      this.filterQuery = "";
      this.tagSearchQuery = "";
      this.folderSearchQuery = "";
    });
  };

  /**
   * Get selected notes
   */
  get selectedNotes(): ImportableNote[] {
    return this.notes.filter(note => note.isSelected);
  }

  /**
   * Get count of selected notes
   */
  get selectedNotesCount(): number {
    return this.selectedNotes.length;
  }

  /**
   * Get count of total notes
   */
  get totalNotesCount(): number {
    return this.notes.length;
  }

  /**
   * Get count of selectable (non-locked) notes
   */
  get selectableNotesCount(): number {
    return this.notes.filter(note => !note.isLocked).length;
  }

  /**
   * Get notes sorted by updatedAt (most recent first)
   */
  get sortedNotes(): ImportableNote[] {
    return [...this.notes].sort((a, b) => {
      if (!a.updatedAt && !b.updatedAt) return 0;
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }

  /**
   * Get notes filtered by the search query (fuzzy matching on titles).
   * Uses pre-computed noteTitles array for optimal performance.
   */
  get filteredNotes(): ImportableNote[] {
    if (!this.filterQuery.trim()) {
      return this.notes;
    }

    const indexes = this.fuzzy.filter(this.noteTitles, this.filterQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.notes[index]);
  }

  /**
   * Get unique folder names from all notes (flattened from all folder paths)
   */
  get folders(): string[] {
    const folderSet = new Set<string>();
    this.notes.forEach(note => {
      if (note.folders) {
        note.folders.forEach(folder => folderSet.add(folder));
      }
    });
    return Array.from(folderSet).sort();
  }

  /**
   * Get unique tag names from all notes
   */
  get tags(): string[] {
    const tagSet = new Set<string>();
    this.notes.forEach(note => {
      if (note.tags) {
        note.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }

  /**
   * Get filtered folders using fuzzy search
   */
  get filteredFolders(): string[] {
    if (!this.folderSearchQuery.trim()) {
      return this.folders;
    }

    const indexes = this.fuzzy.filter(this.folders, this.folderSearchQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.folders[index]);
  }

  /**
   * Get filtered tags using fuzzy search
   */
  get filteredTags(): string[] {
    if (!this.tagSearchQuery.trim()) {
      return this.tags;
    }

    const indexes = this.fuzzy.filter(this.tags, this.tagSearchQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.tags[index]);
  }

  /**
   * Get folders with their associated collection IDs
   */
  get importableFolders(): ImportableFolder[] {
    return this.folders.map(name => ({
      name,
      collectionId: this.folderToCollectionId.get(name)!,
    }));
  }

  /**
   * Get tags with their associated collection IDs
   */
  get importableTags(): ImportableFolder[] {
    return this.tags.map(name => ({
      name,
      collectionId: this.tagToCollectionId.get(name)!,
    }));
  }

  /**
   * Get notes grouped by section (last folder in path), sorted according to sortOption.
   * Notes without folders are grouped under an empty string key.
   * Both notes within sections and sections themselves are sorted based on sortOption.
   * Empty sections (after filtering) are not included.
   * Returns empty array if there are no folders at all (only unfiled notes).
   */
  get notesGroupedByFolder(): FolderGroup[] {
    const groups = new Map<string, ImportableNote[]>();

    // Group filtered notes by section (last folder)
    this.filteredNotes.forEach(note => {
      const section = this.getSectionForNote(note);
      if (!groups.has(section)) {
        groups.set(section, []);
      }
      groups.get(section)!.push(note);
    });

    // If there are no folders at all (only the unfiled section exists), return empty array
    if (groups.size === 1 && groups.has("")) {
      return [];
    }

    // Sort notes within each group based on sortOption
    groups.forEach(notes => {
      switch (this.sortOption) {
        case SortByKind.LastModified:
          notes.sort((a, b) => {
            if (!a.updatedAt && !b.updatedAt) return 0;
            if (!a.updatedAt) return 1;
            if (!b.updatedAt) return -1;
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          });
          break;
        case SortByKind.LastCreated:
          notes.sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0;
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          break;
        case SortByKind.Alphabetical:
          notes.sort((a, b) => {
            return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
          });
          break;
      }
    });

    // Convert to array and sort sections based on sortOption
    // Always put "Unfiled" (empty string section) at the bottom
    const result: FolderGroup[] = [];
    const allSections = Array.from(groups.keys());
    const unfiledSection = allSections.find(section => section === "");
    const namedSections = allSections.filter(section => section !== "");

    const sortedSections = namedSections.sort((a, b) => {
      const notesA = groups.get(a)!;
      const notesB = groups.get(b)!;

      switch (this.sortOption) {
        case SortByKind.LastModified: {
          // Sort sections by most recent updatedAt in each section
          const mostRecentA = notesA.reduce((latest, note) => {
            if (!note.updatedAt) return latest;
            if (!latest) return note.updatedAt;
            return new Date(note.updatedAt) > new Date(latest) ? note.updatedAt : latest;
          }, notesA[0]?.updatedAt);

          const mostRecentB = notesB.reduce((latest, note) => {
            if (!note.updatedAt) return latest;
            if (!latest) return note.updatedAt;
            return new Date(note.updatedAt) > new Date(latest) ? note.updatedAt : latest;
          }, notesB[0]?.updatedAt);

          if (!mostRecentA && !mostRecentB) return 0;
          if (!mostRecentA) return 1;
          if (!mostRecentB) return -1;
          return new Date(mostRecentB).getTime() - new Date(mostRecentA).getTime();
        }
        case SortByKind.LastCreated: {
          // Sort sections by most recent createdAt in each section
          const mostRecentA = notesA.reduce((latest, note) => {
            if (!note.createdAt) return latest;
            if (!latest) return note.createdAt;
            return new Date(note.createdAt) > new Date(latest) ? note.createdAt : latest;
          }, notesA[0]?.createdAt);

          const mostRecentB = notesB.reduce((latest, note) => {
            if (!note.createdAt) return latest;
            if (!latest) return note.createdAt;
            return new Date(note.createdAt) > new Date(latest) ? note.createdAt : latest;
          }, notesB[0]?.createdAt);

          if (!mostRecentA && !mostRecentB) return 0;
          if (!mostRecentA) return 1;
          if (!mostRecentB) return -1;
          return new Date(mostRecentB).getTime() - new Date(mostRecentA).getTime();
        }
        case SortByKind.Alphabetical:
          // Sort sections alphabetically (case-insensitive)
          return a.localeCompare(b, undefined, { sensitivity: "base" });
        default:
          // Default to alphabetical sort
          return a.localeCompare(b, undefined, { sensitivity: "base" });
      }
    });

    // Add unfiled section at the end if it exists
    if (unfiledSection !== undefined) {
      sortedSections.push(unfiledSection);
    }

    sortedSections.forEach(section => {
      const notes = groups.get(section)!;
      // Get collection IDs from the first note's folders (all notes in same section share the same path)
      const firstNote = notes[0];
      const collectionIds =
        firstNote.folders?.map(folder => this.folderToCollectionId.get(folder)!) || [];

      result.push({
        section,
        collectionIds,
        notes,
      });
    });

    return result;
  }
}
